// src/pages/RequestDetails.jsx
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";

import api from "../api/axios";

import {
  FiArrowLeft,
  FiChevronLeft,
  FiChevronRight,
  FiRefreshCcw,
  FiTrash2,
  FiUser,
  FiClock,
  FiChevronDown,
} from "react-icons/fi";

/* ----------------------------- model enums (from Request.js) ----------------------------- */

const STATUS_OPTIONS = [
  { label: "New", value: "new" },
  { label: "Triaged", value: "triaged" },
  { label: "Contacted", value: "contacted" },
  { label: "Qualified", value: "qualified" },
  { label: "Unqualified", value: "unqualified" },
  { label: "Converted", value: "converted" },
  { label: "Closed", value: "closed" },
  { label: "Spam", value: "spam" },
];

const CHANNEL_OPTIONS = [
  { label: "Web", value: "web" },
  { label: "Phone", value: "phone" },
  { label: "Email", value: "email" },
  { label: "SMS", value: "sms" },
  { label: "Referral", value: "referral" },
  { label: "Walk-in", value: "walk_in" },
  { label: "Other", value: "other" },
];

const SOURCE_OPTIONS = [
  { label: "Widget", value: "widget" },
  { label: "Manual", value: "manual" },
  { label: "Email", value: "email" },
  { label: "Import", value: "import" },
  { label: "API", value: "api" },
];

const PRIORITY_OPTIONS = [
  { label: "Low", value: "low" },
  { label: "Normal", value: "normal" },
  { label: "High", value: "high" },
  { label: "Urgent", value: "urgent" },
];

/* ----------------------------- time window display ----------------------------- */

const TIME_WINDOW_DISPLAY = {
  "9-12": "Morning (9:00 AM – 12:00 PM)",
  "1-4": "Afternoon (1:00 PM – 4:00 PM)",
  "4-7": "Evening (4:00 PM – 7:00 PM)",
  anytime: "Anytime",
};

const TIME_WINDOW_PRESETS = [
  { label: TIME_WINDOW_DISPLAY["9-12"], value: "9-12" },
  { label: TIME_WINDOW_DISPLAY["1-4"], value: "1-4" },
  { label: TIME_WINDOW_DISPLAY["4-7"], value: "4-7" },
  { label: TIME_WINDOW_DISPLAY.anytime, value: "anytime" },
];

/* ----------------------------- helpers ----------------------------- */

const resolveId = (row) => row?._id || row?.id || row?.requestId || null;

function safeString(v) {
  return typeof v === "string" ? v : v == null ? "" : String(v);
}

function fmtDateTime(ts) {
  if (!ts) return "—";
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "numeric",
    minute: "2-digit",
  });
}

function humanizeEnum(v) {
  const s = String(v || "").trim();
  if (!s) return "—";
  return s.replace(/_/g, " ").replace(/\b\w/g, (m) => m.toUpperCase());
}

function statusChipClass(v) {
  const s = String(v || "").toLowerCase();
  switch (s) {
    case "new":
      return "bg-blue-50 text-blue-700 border border-blue-200";
    case "triaged":
      return "bg-indigo-50 text-indigo-700 border border-indigo-200";
    case "contacted":
      return "bg-amber-50 text-amber-700 border border-amber-200";
    case "qualified":
      return "bg-green-50 text-green-700 border border-green-200";
    case "unqualified":
      return "bg-slate-100 text-slate-700 border border-slate-200";
    case "converted":
      return "bg-emerald-50 text-emerald-700 border border-emerald-200";
    case "closed":
      return "bg-gray-100 text-gray-700 border border-gray-200";
    case "spam":
      return "bg-rose-50 text-rose-700 border border-rose-200";
    default:
      return "bg-slate-100 text-slate-700 border border-slate-200";
  }
}

function priorityChipClass(v) {
  const s = String(v || "").toLowerCase();
  switch (s) {
    case "urgent":
      return "bg-rose-50 text-rose-700 border border-rose-200";
    case "high":
      return "bg-amber-50 text-amber-700 border border-amber-200";
    case "normal":
      return "bg-slate-100 text-slate-700 border border-slate-200";
    case "low":
      return "bg-gray-50 text-gray-700 border border-gray-200";
    default:
      return "bg-slate-100 text-slate-700 border border-slate-200";
  }
}

function fmtDurationShort(ms) {
  if (!Number.isFinite(ms) || ms < 0) return "—";
  const mins = Math.floor(ms / 60000);
  if (mins < 60) return `${mins}m`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 48) return `${hrs}h`;
  const days = Math.floor(hrs / 24);
  return `${days}d`;
}

function normalizeTagsToString(tags) {
  if (Array.isArray(tags)) return tags.join(", ");
  return safeString(tags || "").trim();
}

function tagsStringToArray(tagsStr) {
  return (safeString(tagsStr) || "")
    .split(",")
    .map((t) => t.trim())
    .filter(Boolean);
}

function uniqLower(arr) {
  const out = [];
  const seen = new Set();
  (Array.isArray(arr) ? arr : []).forEach((x) => {
    const s = safeString(x).trim();
    if (!s) return;
    const k = s.toLowerCase();
    if (seen.has(k)) return;
    seen.add(k);
    out.push(s);
  });
  return out;
}

function ymdToNoonUtcIso(ymd) {
  const s = safeString(ymd).trim();
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (!m) return null;
  const y = Number(m[1]);
  const mo = Number(m[2]);
  const d = Number(m[3]);
  if (!y || !mo || !d) return null;
  return new Date(Date.UTC(y, mo - 1, d, 12, 0, 0)).toISOString();
}

function isoToYmdUtc(iso) {
  const dd = new Date(iso);
  if (Number.isNaN(dd.getTime())) return "";
  const yyyy = dd.getUTCFullYear();
  const mm = String(dd.getUTCMonth() + 1).padStart(2, "0");
  const day = String(dd.getUTCDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${day}`;
}

function prettyTimeWindow(v) {
  const raw = safeString(v).trim();
  if (!raw) return "—";

  const key = raw.toLowerCase();
  if (TIME_WINDOW_DISPLAY[key]) return TIME_WINDOW_DISPLAY[key];

  const m =
    /^(\d{1,2})(?::(\d{2}))?\s*(am|pm)?\s*[-–]\s*(\d{1,2})(?::(\d{2}))?\s*(am|pm)?$/i.exec(
      raw
    );
  if (!m) return raw;

  let sh = Number(m[1]);
  let sm = Number(m[2] || "0");
  let sap = (m[3] || "").toLowerCase();
  let eh = Number(m[4]);
  let em = Number(m[5] || "0");
  let eap = (m[6] || "").toLowerCase();

  if (
    !Number.isFinite(sh) ||
    !Number.isFinite(sm) ||
    !Number.isFinite(eh) ||
    !Number.isFinite(em) ||
    sh < 1 ||
    sh > 12 ||
    eh < 1 ||
    eh > 12 ||
    sm < 0 ||
    sm > 59 ||
    em < 0 ||
    em > 59
  ) {
    return raw;
  }

  const hasAnyMeridian = !!sap || !!eap;

  if (!hasAnyMeridian) {
    if (eh < sh || (eh === sh && em < sm)) {
      sap = "am";
      eap = "pm";
    } else {
      if (sh >= 8 && sh <= 11) sap = "am";
      else sap = "pm";

      if (eh === 12) eap = "pm";
      else if (eh >= 8 && eh <= 11) eap = "am";
      else eap = "pm";
    }
  } else {
    if (sap && !eap) {
      if (sap === "am" && (eh < sh || (eh === sh && em < sm))) eap = "pm";
      else eap = sap;
    } else if (!sap && eap) {
      if (eap === "pm" && (eh < sh || (eh === sh && em < sm))) sap = "am";
      else sap = eap;
    }
  }

  const fmt = (h, min, ap) => {
    const hh = String(h);
    const mm = String(min).padStart(2, "0");
    return `${hh}:${mm} ${String(ap || "").toUpperCase()}`.trim();
  };

  return `${fmt(sh, sm, sap)} – ${fmt(eh, em, eap)}`;
}

/** Extract a best-effort single-line address (raw) from a request payload. */
function extractAddressRaw(r) {
  if (!r || typeof r !== "object") return "";
  const loc = r.location && typeof r.location === "object" ? r.location : null;
  const addr = r.address && typeof r.address === "object" ? r.address : null;

  const raw = safeString(loc?.raw || addr?.raw || "").trim();
  if (raw) return raw;

  const partsFromLoc = [
    loc?.address1,
    loc?.address2,
    loc?.city,
    loc?.region,
    loc?.postalCode,
    loc?.country,
  ]
    .map((x) => safeString(x).trim())
    .filter(Boolean);

  if (partsFromLoc.length) return partsFromLoc.join(", ");

  const partsFromAddr = [
    addr?.street,
    addr?.street2,
    addr?.city,
    addr?.state,
    addr?.zip,
    addr?.postalCode,
    addr?.country,
  ]
    .map((x) => safeString(x).trim())
    .filter(Boolean);

  if (partsFromAddr.length) return partsFromAddr.join(", ");

  if (typeof r.address === "string") return safeString(r.address).trim();

  return "";
}

/* ----------------------------- tiny UI primitives ----------------------------- */

function Card({ title, right, children, className = "" }) {
  return (
    <div className={`rounded-2xl border border-slate-200 bg-white shadow-sm ${className}`}>
      {(title || right) && (
        <div className="px-5 sm:px-6 pt-5 sm:pt-6">
          <div className="flex items-center justify-between gap-3">
            <h2 className="text-lg font-bold text-slate-900">{title}</h2>
            {right}
          </div>
        </div>
      )}
      <div
        className={`${
          title || right ? "px-5 sm:px-6 pb-5 sm:pb-6 pt-4" : "p-5 sm:p-6"
        }`}
      >
        {children}
      </div>
    </div>
  );
}

function Pill({ className = "", children, title }) {
  return (
    <span
      title={title}
      className={`inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-semibold ${className}`}
    >
      {children}
    </span>
  );
}

function FieldLabel({ children }) {
  return <label className="text-xs font-semibold text-slate-600">{children}</label>;
}

function TextInput(props) {
  return (
    <input
      {...props}
      className={[
        "mt-1 w-full px-3 py-2 text-sm border rounded-xl bg-white",
        "focus:outline-none focus:ring-4 focus:ring-blue-100",
        "disabled:bg-slate-50",
        props.className || "",
      ].join(" ")}
    />
  );
}

function TextArea(props) {
  return (
    <textarea
      {...props}
      className={[
        "mt-1 w-full px-3 py-2 text-sm border rounded-xl bg-white",
        "focus:outline-none focus:ring-4 focus:ring-blue-100",
        "disabled:bg-slate-50",
        props.className || "",
      ].join(" ")}
    />
  );
}

function Select({ value, onChange, disabled, children }) {
  return (
    <div className="relative mt-1">
      <select
        className="w-full pl-3 pr-8 py-2 text-sm border rounded-xl bg-white appearance-none disabled:bg-slate-50"
        value={value}
        onChange={onChange}
        disabled={disabled}
      >
        {children}
      </select>
      <FiChevronDown className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-slate-400" />
    </div>
  );
}

function EmptyDash({ children }) {
  const s = safeString(children).trim();
  return <span className={s ? "" : "text-slate-400"}>{s || "—"}</span>;
}

/* ----------------------------- page ----------------------------- */

export default function RequestDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [userRole, setUserRole] = useState("viewer");
  const [meId, setMeId] = useState(null);

  const canMutate = userRole === "admin" || userRole === "staff" || userRole === "owner";
  const canDelete = userRole === "admin" || userRole === "owner";

  const [loading, setLoading] = useState(true);
  const [patching, setPatching] = useState(false);

  const [request, setRequest] = useState(null);
  const [users, setUsers] = useState([]);

  // ✅ RIGHT-SIDE linking state
  const [clientLinkQuery, setClientLinkQuery] = useState("");
  const [clientLinkLoading, setClientLinkLoading] = useState(false);
  const [clientLinkResults, setClientLinkResults] = useState([]);
  const [clientLinkError, setClientLinkError] = useState("");

  // ✅ NEW: create+link loading
  const [creatingClientLink, setCreatingClientLink] = useState(false);

  const [form, setForm] = useState({
    status: "new",
    statusReason: "",
    priority: "normal",
    channel: "web",
    source: "manual",

    customerName: "",
    customerEmail: "",
    customerPhone: "",

    // ✅ editable address (raw single field)
    address: "",

    // legacy fields (display-only)
    serviceName: "",
    priceType: null,
    priceAmount: null,

    // keep description visible (read-only)
    description: "",

    tags: "",

    assignedTo: "",

    preferredDates: [],
    preferredTimeWindows: [],
    preferredScheduleNotes: "",
  });

  const [newDate, setNewDate] = useState("");
  const [customWindow, setCustomWindow] = useState("");

  const [noteText, setNoteText] = useState("");
  const [savingNote, setSavingNote] = useState(false);

  const navCtx = useMemo(() => {
    const st = location?.state || {};
    const ids = Array.isArray(st.requestIds) ? st.requestIds.map(String) : null;
    const from = typeof st.from === "string" ? st.from : null;

    if (!ids || !ids.length) return { has: false };
    const cur = String(id || "");
    const idx = ids.findIndex((x) => String(x) === cur);
    if (idx < 0) return { has: false };

    return {
      has: true,
      ids,
      idx,
      prevId: idx > 0 ? ids[idx - 1] : null,
      nextId: idx < ids.length - 1 ? ids[idx + 1] : null,
      from,
    };
  }, [location?.state, id]);

  const normalizeOne = (resp) => {
    const d = resp?.data?.data ?? resp?.data ?? resp;
    return d && typeof d === "object" ? d : null;
  };

  const fetchProfile = useCallback(async () => {
    try {
      const res = await api.get("/auth/profile");
      const p = res?.data?.data || res?.data || {};
      const role = String(p?.role || "viewer").toLowerCase();
      setUserRole(role);
      setMeId(String(p?._id || p?.id || p?.userId || "") || null);
      try {
        localStorage.setItem("profile", JSON.stringify(p || {}));
      } catch {}
    } catch {
      try {
        const p = JSON.parse(localStorage.getItem("profile") || "{}");
        const role = String(p?.role || "viewer").toLowerCase();
        setUserRole(role);
        setMeId(String(p?._id || p?.id || p?.userId || "") || null);
      } catch {
        setUserRole("viewer");
        setMeId(null);
      }
    }
  }, []);

  const fetchUsers = useCallback(async () => {
    if (!canMutate) return;
    try {
      const res = await api.get("/users", { params: { limit: 200 } });
      const d = res?.data?.data ?? res?.data;
      const items = Array.isArray(d)
        ? d
        : Array.isArray(d?.items)
        ? d.items
        : Array.isArray(d?.data)
        ? d.data
        : [];
      setUsers(Array.isArray(items) ? items : []);
    } catch {
      setUsers([]);
    }
  }, [canMutate]);

  const fetchRequest = useCallback(
    async (opts = {}) => {
      if (!id) return;
      const silent = !!opts.silent;

      if (!silent) setLoading(true);

      try {
        const res = await api.get(`/requests/${id}`);
        const one = normalizeOne(res);
        if (!one) throw new Error("Request not found");

        setRequest(one);

        const cust = one?.customer || {};
        const customerName = safeString(cust?.name || one?.contactName || "").trim();
        const customerEmail = safeString(cust?.email || one?.email || "").trim();
        const customerPhone = safeString(cust?.phone || one?.phone || "").trim();

        const pref = one?.preferredSchedule || {};
        const prefDates = Array.isArray(pref?.dates)
          ? pref.dates.map((d) => isoToYmdUtc(d)).filter(Boolean)
          : [];

        const addressRaw = extractAddressRaw(one);

        const nextForm = {
          status: safeString(one?.status || "new").toLowerCase(),
          statusReason: safeString(one?.statusReason || "").trim(),
          priority: safeString(one?.priority || "normal").toLowerCase(),
          channel: safeString(one?.channel || "web").toLowerCase(),
          source: safeString(one?.source || "manual").toLowerCase(),

          customerName,
          customerEmail,
          customerPhone,

          address: addressRaw,

          serviceName: safeString(one?.serviceName || one?.serviceType || "").trim(),
          priceType: one?.priceType ?? null,
          priceAmount:
            typeof one?.priceAmount === "number" ? one.priceAmount : one?.priceAmount ?? null,

          description: safeString(one?.description || "").trim(),

          tags: normalizeTagsToString(one?.tags),

          assignedTo: safeString(one?.assignedTo?._id || one?.assignedTo || ""),

          preferredDates: prefDates,
          preferredTimeWindows: Array.isArray(pref?.timeWindows)
            ? pref.timeWindows.map((x) => safeString(x).trim()).filter(Boolean)
            : [],
          preferredScheduleNotes: safeString(pref?.notes || "").trim(),
        };

        setForm(nextForm);

        // ✅ helpful default for linking search (don’t auto-search until user types)
        setClientLinkQuery((q) => q || "");
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(e);
        toast.error(e?.message || "Failed to load request");
        setRequest(null);
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [id]
  );

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  useEffect(() => {
    fetchRequest();
    setNewDate("");
    setCustomWindow("");
    setNoteText("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchRequest, id]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  // ----- services (read-only) -----
  const derivedServices = useMemo(() => {
    const r = request || {};

    const fromNamesArray = Array.isArray(r?.serviceNames) ? r.serviceNames : [];
    const fromEmbeddedNames = Array.isArray(r?.services)
      ? r.services.map((s) => s?.name || s?.title || s?.label || "").filter(Boolean)
      : [];
    const legacy = safeString(r?.serviceName || r?.serviceType || form.serviceName || "").trim();

    const names = uniqLower([
      ...(fromNamesArray || []),
      ...(fromEmbeddedNames || []),
      ...(legacy ? [legacy] : []),
    ]);

    const details = Array.isArray(r?.services)
      ? r.services
          .map((s) => ({
            name: safeString(s?.name || s?.title || s?.label || "").trim(),
            description: safeString(s?.description || s?.details || "").trim(),
          }))
          .filter((x) => x.name)
      : [];

    const ids =
      Array.isArray(r?.serviceIds) && r.serviceIds.length
        ? r.serviceIds
        : Array.isArray(r?.servicesIds) && r.servicesIds.length
        ? r.servicesIds
        : [];

    return { names, ids: Array.isArray(ids) ? ids : [], details };
  }, [request, form.serviceName]);

  const derived = useMemo(() => {
    const r = request || {};
    const requestNumber = safeString(r?.requestNumber || "").trim() || "—";
    const status = safeString(r?.status || "new").toLowerCase();
    const priority = safeString(r?.priority || "normal").toLowerCase();

    const createdAt = r?.createdAt || null;
    const updatedAt = r?.updatedAt || null;

    const assigned = r?.assignedTo || null;
    const assignedId = typeof assigned === "string" ? assigned : assigned?._id || assigned?.id || null;
    const assignedName =
      typeof assigned === "object" && assigned
        ? assigned?.name || assigned?.fullName || assigned?.email || "Assigned"
        : assignedId
        ? "Assigned"
        : "Unassigned";

    const locationObj =
      r?.location ||
      (r?.address
        ? {
            address1: r?.address?.street,
            city: r?.address?.city,
            region: r?.address?.state,
            postalCode: r?.address?.zip,
            country: r?.address?.country,
            raw: r?.address?.raw,
          }
        : null);

    const hist = Array.isArray(r?.statusHistory) ? r.statusHistory : [];
    const lastChangeAt = hist.length ? hist[hist.length - 1]?.at : r?.updatedAt || r?.createdAt || null;
    const msInStatus = lastChangeAt ? Date.now() - new Date(lastChangeAt).getTime() : NaN;

    const source = safeString(r?.source || form.source || "manual").toLowerCase();
    const channel = safeString(r?.channel || form.channel || "web").toLowerCase();

    const locationLine =
      locationObj &&
      [
        locationObj.address1,
        locationObj.address2,
        locationObj.city,
        locationObj.region,
        locationObj.postalCode,
        locationObj.country,
      ]
        .map((x) => safeString(x).trim())
        .filter(Boolean)
        .join(", ");

    return {
      requestNumber,
      status,
      priority,
      createdAt,
      updatedAt,
      assignedName,
      assignedId: assignedId ? String(assignedId) : "",
      location: locationObj,
      locationLine: locationLine || safeString(locationObj?.raw || "").trim() || "",
      timeInStatus: Number.isFinite(msInStatus) ? fmtDurationShort(msInStatus) : "—",
      source,
      channel,
    };
  }, [request, form.source, form.channel]);

  const preferredSchedulePayloadFromForm = useCallback((f) => {
    const hasPref =
      (Array.isArray(f.preferredDates) && f.preferredDates.length > 0) ||
      (Array.isArray(f.preferredTimeWindows) && f.preferredTimeWindows.length > 0) ||
      !!safeString(f.preferredScheduleNotes).trim();

    if (!hasPref) return null;

    return {
      dates: (f.preferredDates || []).map((s) => ymdToNoonUtcIso(s)).filter(Boolean),
      timeWindows: (f.preferredTimeWindows || [])
        .map((x) => safeString(x).trim())
        .filter(Boolean),
      notes: safeString(f.preferredScheduleNotes).trim() || undefined,
    };
  }, []);

  const patchRequest = useCallback(
    async (patch, opts = {}) => {
      if (!canMutate) return toast.error("Not allowed");
      if (!request) return;

      const { toastSuccess, silent = true } = opts;

      setPatching(true);
      try {
        await api.put(`/requests/${resolveId(request)}`, patch);
        if (toastSuccess) toast.success(toastSuccess);
        await fetchRequest({ silent });
      } catch (e) {
        toast.error(e?.message || "Update failed");
      } finally {
        setPatching(false);
      }
    },
    [canMutate, request, fetchRequest]
  );

  // ✅ address auto-patch on blur (updates address.raw)
  const patchAddressFromForm = useCallback(async () => {
    if (!request) return;

    const nextRaw = safeString(form.address).trim();
    const currentRaw = safeString(extractAddressRaw(request)).trim();

    if (nextRaw === currentRaw) return;

    const existingAddr =
      request?.address && typeof request.address === "object" ? request.address : null;

    const patch = {
      address: nextRaw ? { ...(existingAddr || {}), raw: nextRaw } : null,
    };

    await patchRequest(patch, { silent: true });
  }, [form.address, patchRequest, request]);

  // ---- workflow updates ----
  const onAssign = useCallback(
    async (userIdOrNull) => {
      await patchRequest(
        { assignedTo: userIdOrNull || null },
        { toastSuccess: userIdOrNull ? "Assigned" : "Unassigned", silent: true }
      );
    },
    [patchRequest]
  );

  const onDelete = useCallback(async () => {
    if (!canDelete) return toast.error("Not allowed");
    if (!request) return;
    if (!window.confirm("Delete this request? This cannot be undone.")) return;

    try {
      await api.delete(`/requests/${resolveId(request)}`);
      toast.success("Deleted");
      navigate(navCtx?.from || "/requests");
    } catch (e) {
      toast.error(e?.message || "Delete failed");
    }
  }, [canDelete, request, navigate, navCtx?.from]);

  // ---- schedule editing (auto-patch) ----
  const patchScheduleFromNextForm = useCallback(
    async (nextForm) => {
      const preferredSchedule = preferredSchedulePayloadFromForm(nextForm);
      await patchRequest({ preferredSchedule }, { silent: true });
    },
    [patchRequest, preferredSchedulePayloadFromForm]
  );

  const onAddPreferredDate = useCallback(() => {
    const d = safeString(newDate).trim();
    if (!d) return;

    setForm((f) => {
      const set = new Set([...(f.preferredDates || []), d]);
      const next = { ...f, preferredDates: Array.from(set).sort() };
      patchScheduleFromNextForm(next);
      return next;
    });
    setNewDate("");
  }, [newDate, patchScheduleFromNextForm]);

  const onRemovePreferredDate = useCallback(
    (d) => {
      setForm((f) => {
        const next = { ...f, preferredDates: (f.preferredDates || []).filter((x) => x !== d) };
        patchScheduleFromNextForm(next);
        return next;
      });
    },
    [patchScheduleFromNextForm]
  );

  const onToggleTimeWindow = useCallback(
    (w) => {
      const v = safeString(w).trim();
      if (!v) return;

      setForm((f) => {
        const cur = new Set(f.preferredTimeWindows || []);
        if (cur.has(v)) cur.delete(v);
        else cur.add(v);
        const next = { ...f, preferredTimeWindows: Array.from(cur) };
        patchScheduleFromNextForm(next);
        return next;
      });
    },
    [patchScheduleFromNextForm]
  );

  const onAddCustomWindow = useCallback(() => {
    const v = safeString(customWindow).trim();
    if (!v) return;

    setForm((f) => {
      const cur = new Set(f.preferredTimeWindows || []);
      cur.add(v);
      const next = { ...f, preferredTimeWindows: Array.from(cur) };
      patchScheduleFromNextForm(next);
      return next;
    });
    setCustomWindow("");
  }, [customWindow, patchScheduleFromNextForm]);

  const onRemoveTimeWindow = useCallback(
    (v) => {
      setForm((f) => {
        const next = { ...f, preferredTimeWindows: (f.preferredTimeWindows || []).filter((x) => x !== v) };
        patchScheduleFromNextForm(next);
        return next;
      });
    },
    [patchScheduleFromNextForm]
  );

  const onBlurScheduleNotes = useCallback(() => {
    const nextForm = { ...form, preferredScheduleNotes: safeString(form.preferredScheduleNotes) };
    patchScheduleFromNextForm(nextForm);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.preferredScheduleNotes, patchScheduleFromNextForm]);

  // ---- contact editing (auto-patch on blur) ----
  const patchClientFromForm = useCallback(async () => {
    const customerName = safeString(form.customerName).trim();
    const customerEmail = safeString(form.customerEmail).trim();
    const customerPhone = safeString(form.customerPhone).trim();

    const customer =
      customerName || customerEmail || customerPhone
        ? {
            name: customerName || undefined,
            email: customerEmail || undefined,
            phone: customerPhone || undefined,
          }
        : null;

    await patchRequest(
      {
        customer,
        contactName: customerName,
        email: customerEmail,
        phone: customerPhone,
      },
      { silent: true }
    );
  }, [form.customerName, form.customerEmail, form.customerPhone, patchRequest]);

  // ---- workflow fields (auto-patch) ----
  const onWorkflowChange = useCallback(
    (patch, nextPartialForm, successToast = null) => {
      setForm((f) => ({ ...f, ...nextPartialForm }));
      patchRequest(patch, { toastSuccess: successToast, silent: true });
    },
    [patchRequest]
  );

  const onBlurStatusReason = useCallback(() => {
    patchRequest({ statusReason: safeString(form.statusReason).trim() || undefined }, { silent: true });
  }, [form.statusReason, patchRequest]);

  const onBlurTags = useCallback(() => {
    const tagsArr = tagsStringToArray(form.tags);
    patchRequest({ tags: tagsArr }, { silent: true });
  }, [form.tags, patchRequest]);

  // ---- notes ----
  const onAddNote = useCallback(async () => {
    if (!canMutate) return toast.error("Not allowed");
    if (!request) return;

    const text = safeString(noteText).trim();
    if (!text) return;

    setSavingNote(true);
    try {
      const existing = Array.isArray(request?.notes) ? request.notes : [];
      const nextNotes = [...existing, { text }];
      await api.put(`/requests/${resolveId(request)}`, { notes: nextNotes });
      toast.success("Note added");
      setNoteText("");
      await fetchRequest({ silent: true });
    } catch (e) {
      toast.error(e?.message || "Failed to add note");
    } finally {
      setSavingNote(false);
    }
  }, [canMutate, request, noteText, fetchRequest]);

  /* ----------------------------- RIGHT-SIDE: Linked client card logic ----------------------------- */

  const linkedClient = useMemo(() => {
    const r = request || {};
    const raw = r?.clientId ?? r?.client ?? null;

    let clientId = "";
    let clientName = safeString(r?.clientName || "").trim();
    let clientEmail = "";
    let clientPhone = "";

    if (typeof raw === "string") {
      clientId = raw;
    } else if (raw && typeof raw === "object") {
      clientId = safeString(raw?._id || raw?.id || "").trim();
      clientName =
        safeString(raw?.name || raw?.fullName || "").trim() ||
        clientName ||
        safeString(raw?.email || "").trim() ||
        "Client";
      clientEmail = safeString(raw?.email || "").trim();
      clientPhone = safeString(raw?.phone || "").trim();
    }

    // sometimes backend stores clientId as objectId string in r.clientId, and name in r.clientName
    if (!clientId && typeof r?.clientId === "string") clientId = safeString(r.clientId).trim();
    if (!clientName) clientName = safeString(r?.clientName || "").trim();

    const isLinked = !!clientId;
    return {
      isLinked,
      clientId: isLinked ? clientId : "",
      clientName: clientName || "",
      clientEmail,
      clientPhone,
    };
  }, [request]);

  const normalizeClientsFromListResponse = (res) => {
    const d = res?.data?.data ?? res?.data;
    const items = Array.isArray(d)
      ? d
      : Array.isArray(d?.items)
      ? d.items
      : Array.isArray(d?.data)
      ? d.data
      : Array.isArray(d?.results)
      ? d.results
      : [];
    return Array.isArray(items) ? items : [];
  };

  // debounced client search
  useEffect(() => {
    if (!canMutate) return;

    const q = safeString(clientLinkQuery).trim();
    setClientLinkError("");

    if (q.length < 2) {
      setClientLinkResults([]);
      setClientLinkLoading(false);
      return;
    }

    let alive = true;
    setClientLinkLoading(true);

    const t = setTimeout(async () => {
      try {
        const res = await api.get("/clients", {
          params: {
            q,
            search: q, // some backends prefer "search"
            limit: 8,
            pageSize: 8,
          },
        });

        const items = normalizeClientsFromListResponse(res);

        const mapped = items
          .map((c) => {
            const cid = safeString(c?._id || c?.id || c?.clientId || "").trim();
            if (!cid) return null;
            const name = safeString(c?.name || c?.fullName || c?.companyName || "").trim() || "Client";
            const email = safeString(c?.email || "").trim();
            const phone = safeString(c?.phone || "").trim();
            const company = safeString(c?.company || c?.businessName || "").trim();
            return { id: cid, name, email, phone, company };
          })
          .filter(Boolean);

        if (!alive) return;
        setClientLinkResults(mapped);
      } catch (e) {
        if (!alive) return;
        setClientLinkResults([]);
        setClientLinkError("Could not search clients.");
      } finally {
        if (!alive) return;
        setClientLinkLoading(false);
      }
    }, 250);

    return () => {
      alive = false;
      clearTimeout(t);
    };
  }, [clientLinkQuery, canMutate]);

  const onLinkClient = useCallback(
    async (c) => {
      if (!c || !c.id) return;
      if (!request) return;

      await patchRequest(
        {
          clientId: c.id,
          clientName: c.name || safeString(form.customerName).trim() || undefined,
        },
        { toastSuccess: "Client linked", silent: true }
      );

      setClientLinkQuery("");
      setClientLinkResults([]);
      setClientLinkError("");
    },
    [patchRequest, request, form.customerName]
  );

  const onUnlinkClient = useCallback(async () => {
    if (!request) return;
    await patchRequest(
      { clientId: null, clientName: null },
      { toastSuccess: "Client unlinked", silent: true }
    );
  }, [patchRequest, request]);

  const onQuickSearchFromContact = useCallback(
    (mode) => {
      const name = safeString(form.customerName).trim();
      const email = safeString(form.customerEmail).trim();
      const phone = safeString(form.customerPhone).trim();

      if (mode === "email" && email) return setClientLinkQuery(email);
      if (mode === "phone" && phone) return setClientLinkQuery(phone);
      if (name) return setClientLinkQuery(name);
      if (email) return setClientLinkQuery(email);
      if (phone) return setClientLinkQuery(phone);

      toast.info("No contact info to search.");
    },
    [form.customerName, form.customerEmail, form.customerPhone]
  );

  // ✅ NEW: one-click create client & link
  // Expects backend endpoint: POST /requests/:id/link-client
  const onCreateClientAndLink = useCallback(async () => {
    if (!canMutate) return toast.error("Not allowed");
    if (!request) return;
    if (linkedClient.isLinked) return;

    const name = safeString(form.customerName).trim();
    const email = safeString(form.customerEmail).trim();
    const phone = safeString(form.customerPhone).trim();
    const addressRaw = safeString(form.address).trim();

    if (!name && !email && !phone) {
      return toast.error("Add at least a name, email, or phone first.");
    }

    setCreatingClientLink(true);
    try {
      // minimal payload (backend can also infer from the request itself)
      const payload = {
        name: name || undefined,
        email: email || undefined,
        phone: phone || undefined,
        address: addressRaw ? { raw: addressRaw } : undefined,
      };

      const res = await api.post(`/requests/${resolveId(request)}/link-client`, payload);
      const data = res?.data?.data ?? res?.data ?? {};

      // Optional: if backend returns clientId we can use it, but we don't auto-navigate (keeps behavior minimal)
      const newClientId =
        safeString(data?.clientId || data?.result?.clientId || data?.client?._id || data?.client?.id || "").trim();

      toast.success("Client created & linked");
      setClientLinkQuery("");
      setClientLinkResults([]);
      setClientLinkError("");

      await fetchRequest({ silent: true });

      // If you ever want auto-nav later:
      // if (newClientId) navigate(`/clients/${newClientId}`);
    } catch (e) {
      const msg =
        e?.response?.data?.message ||
        e?.response?.data?.error ||
        e?.message ||
        "Failed to create & link client";
      toast.error(msg);
    } finally {
      setCreatingClientLink(false);
    }
  }, [canMutate, request, linkedClient.isLinked, form.customerName, form.customerEmail, form.customerPhone, form.address, fetchRequest]);

  /* ----------------------------- loading states ----------------------------- */

  if (loading) {
    return (
      <div className="w-full min-h-[90vh] py-8 px-3 sm:px-6 lg:px-8 bg-slate-50">
        <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-6">
          <div className="h-7 w-56 bg-slate-100 rounded animate-pulse" />
          <div className="mt-3 h-4 w-80 bg-slate-100 rounded animate-pulse" />
        </div>

        <div className="mt-4 grid grid-cols-1 lg:grid-cols-12 gap-4">
          <div className="lg:col-span-8 space-y-4">
            <div className="h-[300px] bg-white border border-slate-200 rounded-2xl animate-pulse" />
            <div className="h-[260px] bg-white border border-slate-200 rounded-2xl animate-pulse" />
            <div className="h-[320px] bg-white border border-slate-200 rounded-2xl animate-pulse" />
          </div>
          <div className="lg:col-span-4 space-y-4">
            <div className="h-[320px] bg-white border border-slate-200 rounded-2xl animate-pulse" />
            <div className="h-[200px] bg-white border border-slate-200 rounded-2xl animate-pulse" />
          </div>
        </div>
      </div>
    );
  }

  if (!request) {
    return (
      <div className="w-full min-h-[90vh] py-8 px-3 sm:px-6 lg:px-8 bg-slate-50">
        <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-8 text-center">
          <div className="text-slate-900 font-bold text-lg">Request not found</div>
          <div className="text-slate-500 mt-1">It may have been deleted.</div>
          <button
            className="mt-5 inline-flex items-center gap-2 px-4 py-2 rounded-xl border bg-white hover:bg-slate-50 font-semibold"
            onClick={() => navigate(navCtx?.from || "/requests")}
            type="button"
          >
            <FiArrowLeft /> Back to Requests
          </button>
        </div>
      </div>
    );
  }

  const hasScheduleSummary =
    (form.preferredDates || []).length > 0 ||
    (form.preferredTimeWindows || []).length > 0 ||
    !!safeString(form.preferredScheduleNotes).trim();

  const addressValue = safeString(form.address || derived.locationLine || derived.location?.raw || "").trim();

  return (
    <div className="w-full min-h-[90vh] py-8 px-3 sm:px-6 lg:px-8 bg-slate-50">
      {/* Header (NOT sticky) */}
      <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
        <div className="p-5 sm:p-6">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
            {/* Left header */}
            <div className="min-w-0">
              <div className="flex items-start gap-3">
                <button
                  onClick={() => navigate(navCtx?.from || -1)}
                  className="inline-flex items-center gap-2 px-3 py-2 rounded-xl border bg-white hover:bg-slate-50"
                  title="Back"
                  type="button"
                >
                  <FiArrowLeft /> Back
                </button>

                <div className="min-w-0">
                  <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 truncate">
                    {safeString(request?.requestNumber || "").trim() || "Request"}
                  </h1>

                  <div className="mt-2 flex flex-wrap items-center gap-2 text-sm text-slate-600">
                    <Pill className={statusChipClass(derived.status)} title="Status">
                      {humanizeEnum(derived.status)}
                    </Pill>

                    <Pill className={priorityChipClass(derived.priority)} title="Priority">
                      {humanizeEnum(derived.priority)}
                    </Pill>

                    <Pill className="bg-slate-50 text-slate-700 border border-slate-200" title="Source • Channel">
                      {humanizeEnum(derived.source)} • {humanizeEnum(derived.channel)}
                    </Pill>

                    <span className="text-slate-400">•</span>

                    <span className="inline-flex items-center gap-1">
                      <FiClock /> Updated {fmtDateTime(derived.updatedAt)}
                    </span>

                    {patching ? (
                      <>
                        <span className="text-slate-400">•</span>
                        <span className="text-xs font-semibold text-slate-500">Saving…</span>
                      </>
                    ) : null}
                  </div>

                  <div className="mt-2 text-xs text-slate-500">
                    Services:{" "}
                    <span className="font-semibold text-slate-700">
                      {derivedServices.names.length || derivedServices.ids.length || 0}
                    </span>
                    {derivedServices.names.length ? " selected" : derivedServices.ids.length ? " selected" : ""}
                  </div>
                </div>
              </div>
            </div>

            {/* Right header actions */}
            <div className="flex flex-wrap items-center gap-2 justify-start lg:justify-end">
              {navCtx?.has && (
                <div className="inline-flex items-center rounded-xl border bg-white overflow-hidden">
                  <button
                    className={`px-3 py-2 text-sm font-semibold inline-flex items-center gap-2 ${
                      navCtx.prevId ? "hover:bg-slate-50" : "text-slate-300 cursor-not-allowed"
                    }`}
                    onClick={() => {
                      if (!navCtx.prevId) return;
                      navigate(`/requests/${navCtx.prevId}`, { state: location.state });
                    }}
                    disabled={!navCtx.prevId}
                    title="Previous request"
                    type="button"
                  >
                    <FiChevronLeft /> Prev
                  </button>
                  <div className="w-px bg-slate-200" />
                  <button
                    className={`px-3 py-2 text-sm font-semibold inline-flex items-center gap-2 ${
                      navCtx.nextId ? "hover:bg-slate-50" : "text-slate-300 cursor-not-allowed"
                    }`}
                    onClick={() => {
                      if (!navCtx.nextId) return;
                      navigate(`/requests/${navCtx.nextId}`, { state: location.state });
                    }}
                    disabled={!navCtx.nextId}
                    title="Next request"
                    type="button"
                  >
                    Next <FiChevronRight />
                  </button>
                </div>
              )}

              <button
                onClick={() => fetchRequest({ silent: false })}
                className="inline-flex items-center gap-2 px-3 py-2 rounded-xl border bg-white hover:bg-slate-50"
                title="Refresh"
                disabled={patching}
                type="button"
              >
                <FiRefreshCcw /> Refresh
              </button>

              {canDelete && (
                <button
                  onClick={onDelete}
                  className="inline-flex items-center gap-2 px-3 py-2 rounded-xl border border-rose-200 bg-rose-50 text-rose-700 font-semibold hover:bg-rose-100"
                  title="Delete"
                  disabled={!request || patching}
                  type="button"
                >
                  <FiTrash2 />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Main layout: LEFT content + RIGHT (Linked Client + Workflow + Timeline) */}
      <div className="mt-4 grid grid-cols-1 lg:grid-cols-12 gap-4">
        {/* LEFT */}
        <div className="lg:col-span-8 space-y-4">
          {/* Service Request (READ-ONLY) */}
          <Card title="Service Request" right={<span className="text-xs text-slate-500">Read-only</span>}>
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
              {/* Services + estimate */}
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                <div className="min-w-0">
                  <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Services</div>

                  <div className="mt-2 flex flex-wrap gap-2">
                    {derivedServices.names.length ? (
                      derivedServices.names.map((s) => (
                        <span
                          key={s}
                          className="inline-flex items-center px-2.5 py-1 rounded-xl text-xs font-semibold bg-slate-900 text-white"
                          title="Service"
                        >
                          {s}
                        </span>
                      ))
                    ) : derivedServices.ids.length ? (
                      derivedServices.ids.map((sid) => (
                        <span
                          key={sid}
                          className="inline-flex items-center px-2.5 py-1 rounded-xl text-xs font-semibold bg-white text-slate-800 border border-slate-200"
                          title="Service ID"
                        >
                          {String(sid).length > 14
                            ? `${String(sid).slice(0, 6)}…${String(sid).slice(-6)}`
                            : String(sid)}
                        </span>
                      ))
                    ) : (
                      <div className="text-sm text-slate-400">—</div>
                    )}
                  </div>

                  {derivedServices.details?.some((d) => d.description) ? (
                    <div className="mt-3 space-y-2">
                      {derivedServices.details
                        .filter((d) => d.description)
                        .slice(0, 5)
                        .map((d) => (
                          <div key={`${d.name}-${d.description}`} className="text-sm text-slate-700">
                            <span className="font-semibold text-slate-900">{d.name}:</span>{" "}
                            <span className="whitespace-pre-wrap">{d.description}</span>
                          </div>
                        ))}
                    </div>
                  ) : null}
                </div>

                <div className="shrink-0 rounded-2xl bg-white border border-slate-200 px-4 py-3">
                  <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Estimate</div>
                  <div className="mt-1 text-lg font-extrabold text-slate-900">
                    <EmptyDash>
                      {form.priceAmount == null || form.priceAmount === "" ? "" : String(form.priceAmount)}
                    </EmptyDash>
                  </div>
                  <div className="mt-1 text-xs text-slate-500">
                    <EmptyDash>{form.priceType ? humanizeEnum(form.priceType) : ""}</EmptyDash>
                  </div>
                </div>
              </div>

              {/* Preferred schedule + Description INSIDE this box */}
              <div className="mt-4 pt-4 border-t border-slate-200">
                <div className="flex items-center justify-between">
                  <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Preferred schedule</div>
                  <div className="text-xs text-slate-500">Optional</div>
                </div>

                {hasScheduleSummary ? (
                  <>
                    {(form.preferredDates || []).length > 0 ? (
                      <div className="mt-2 flex flex-wrap gap-2">
                        {(form.preferredDates || []).slice(0, 6).map((d) => (
                          <span
                            key={d}
                            className="inline-flex items-center px-2.5 py-1 rounded-xl text-xs font-semibold bg-white text-slate-800 border border-slate-200"
                            title="Preferred date"
                          >
                            {d}
                          </span>
                        ))}
                        {(form.preferredDates || []).length > 6 ? (
                          <span className="text-xs text-slate-500 self-center">
                            +{(form.preferredDates || []).length - 6} more
                          </span>
                        ) : null}
                      </div>
                    ) : null}

                    {(form.preferredTimeWindows || []).length > 0 ? (
                      <div className="mt-2 flex flex-wrap gap-2">
                        {(form.preferredTimeWindows || []).slice(0, 6).map((w) => (
                          <span
                            key={w}
                            className="inline-flex items-center px-2.5 py-1 rounded-xl text-xs font-semibold bg-slate-100 text-slate-800 border border-slate-200"
                            title="Preferred time window"
                          >
                            {prettyTimeWindow(w)}
                          </span>
                        ))}
                        {(form.preferredTimeWindows || []).length > 6 ? (
                          <span className="text-xs text-slate-500 self-center">
                            +{(form.preferredTimeWindows || []).length - 6} more
                          </span>
                        ) : null}
                      </div>
                    ) : null}

                    {safeString(form.preferredScheduleNotes).trim() ? (
                      <div className="mt-3 text-sm text-slate-700 whitespace-pre-wrap">
                        {safeString(form.preferredScheduleNotes).trim()}
                      </div>
                    ) : null}
                  </>
                ) : (
                  <div className="mt-2 text-sm text-slate-500">No schedule preferences provided.</div>
                )}

                {/* Description inside the same read-only panel */}
                <div className="mt-4 pt-4 border-t border-slate-200">
                  <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Description</div>
                  <div className="mt-2 rounded-2xl border border-slate-200 bg-white p-4">
                    {safeString(form.description).trim() ? (
                      <div className="text-sm text-slate-800 whitespace-pre-wrap">
                        {safeString(form.description).trim()}
                      </div>
                    ) : (
                      <div className="text-sm text-slate-400">No description provided.</div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </Card>

          {/* Contact (fields only) */}
          <Card
            title="Client"
            right={<span className="text-xs text-slate-500">{canMutate ? "Editable" : "Read-only"}</span>}
          >
            <div className="grid grid-cols-1 sm:grid-cols-12 gap-3">
              <div className="sm:col-span-12">
                <FieldLabel>Name</FieldLabel>
                <TextInput
                  value={form.customerName}
                  onChange={(e) => setForm((f) => ({ ...f, customerName: e.target.value }))}
                  onBlur={patchClientFromForm}
                  disabled={!canMutate}
                  placeholder="Customer name"
                  className="text-base font-semibold"
                />
              </div>

              <div className="sm:col-span-6">
                <FieldLabel>Email</FieldLabel>
                <TextInput
                  value={form.customerEmail}
                  onChange={(e) => setForm((f) => ({ ...f, customerEmail: e.target.value }))}
                  onBlur={patchClientFromForm}
                  disabled={!canMutate}
                  placeholder="email@example.com"
                />
              </div>

              <div className="sm:col-span-6">
                <FieldLabel>Phone</FieldLabel>
                <TextInput
                  value={form.customerPhone}
                  onChange={(e) => setForm((f) => ({ ...f, customerPhone: e.target.value }))}
                  onBlur={patchClientFromForm}
                  disabled={!canMutate}
                  placeholder="+1 555 555 5555"
                />
              </div>

              <div className="sm:col-span-12">
                <FieldLabel>Address</FieldLabel>
                <TextArea
                  className="min-h-[84px]"
                  value={addressValue}
                  onChange={(e) => setForm((f) => ({ ...f, address: e.target.value }))}
                  onBlur={patchAddressFromForm}
                  disabled={!canMutate || patching}
                  placeholder="Enter address..."
                />
                <div className="mt-2 text-xs text-slate-500">Saves on blur.</div>
              </div>
            </div>
          </Card>

          {/* Schedule */}
          <Card title="Schedule">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
              <div className="lg:col-span-6">
                <div className="flex items-center justify-between">
                  <div className="text-sm font-bold text-slate-900">Preferred dates</div>
                  <div className="text-xs text-slate-500">YYYY-MM-DD</div>
                </div>

                {canMutate && (
                  <div className="mt-2 flex gap-2">
                    <input
                      type="date"
                      className="flex-1 px-3 py-2 text-sm border rounded-xl bg-white"
                      value={newDate}
                      onChange={(e) => setNewDate(e.target.value)}
                    />
                    <button
                      onClick={onAddPreferredDate}
                      className="px-3 py-2 rounded-xl border bg-white hover:bg-slate-50 text-sm font-semibold"
                      type="button"
                      disabled={patching}
                    >
                      Add
                    </button>
                  </div>
                )}

                <div className="mt-3 flex flex-wrap gap-2">
                  {(form.preferredDates || []).length ? (
                    form.preferredDates.map((d) => (
                      <span
                        key={d}
                        className="inline-flex items-center gap-2 px-3 py-1.5 rounded-xl text-xs font-semibold bg-slate-900 text-white"
                        title="Preferred date"
                      >
                        {d}
                        {canMutate && (
                          <button
                            type="button"
                            className="text-white/80 hover:text-white"
                            onClick={() => onRemovePreferredDate(d)}
                            title="Remove"
                            disabled={patching}
                          >
                            ×
                          </button>
                        )}
                      </span>
                    ))
                  ) : (
                    <div className="text-sm text-slate-400 mt-2">No preferred dates</div>
                  )}
                </div>
              </div>

              <div className="lg:col-span-6">
                <div className="flex items-center justify-between">
                  <div className="text-sm font-bold text-slate-900">Preferred time windows</div>
                  <div className="text-xs text-slate-500">Select any</div>
                </div>

                <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {TIME_WINDOW_PRESETS.map((w) => {
                    const active = (form.preferredTimeWindows || []).includes(w.value);
                    return (
                      <label
                        key={w.value}
                        className={`flex items-start gap-2 text-sm rounded-xl border px-3 py-2 ${
                          active ? "bg-slate-50 border-slate-300" : "bg-white border-slate-200"
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={active}
                          disabled={!canMutate || patching}
                          onChange={() => onToggleTimeWindow(w.value)}
                          className="mt-1"
                        />
                        <span className="text-slate-800 leading-snug">{w.label}</span>
                      </label>
                    );
                  })}
                </div>

                {canMutate && (
                  <div className="mt-3 flex gap-2">
                    <input
                      className="flex-1 px-3 py-2 text-sm border rounded-xl bg-white"
                      value={customWindow}
                      onChange={(e) => setCustomWindow(e.target.value)}
                      placeholder='Custom (e.g. "10-2" or "10am-2pm")'
                      disabled={patching}
                    />
                    <button
                      onClick={onAddCustomWindow}
                      className="px-3 py-2 rounded-xl border bg-white hover:bg-slate-50 text-sm font-semibold"
                      type="button"
                      disabled={patching}
                    >
                      Add
                    </button>
                  </div>
                )}

                {(form.preferredTimeWindows || []).length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {form.preferredTimeWindows.map((v) => (
                      <span
                        key={v}
                        className="inline-flex items-center gap-2 px-3 py-1.5 rounded-xl text-xs font-semibold bg-slate-100 text-slate-800 border border-slate-200"
                        title="Preferred time window"
                      >
                        {prettyTimeWindow(v)}
                        {canMutate && (
                          <button
                            type="button"
                            className="text-slate-500 hover:text-slate-900"
                            onClick={() => onRemoveTimeWindow(v)}
                            title="Remove"
                            disabled={patching}
                          >
                            ×
                          </button>
                        )}
                      </span>
                    ))}
                  </div>
                )}
              </div>

              <div className="lg:col-span-12">
                <FieldLabel>Schedule notes</FieldLabel>
                <TextArea
                  className="min-h-[90px]"
                  value={form.preferredScheduleNotes}
                  onChange={(e) => setForm((f) => ({ ...f, preferredScheduleNotes: e.target.value }))}
                  onBlur={onBlurScheduleNotes}
                  disabled={!canMutate}
                  placeholder="Anything they mentioned about timing..."
                />
              </div>
            </div>
          </Card>

          {/* Notes */}
          <Card title="Notes">
            {Array.isArray(request?.notes) && request.notes.length > 0 ? (
              <div className="space-y-3">
                {request.notes
                  .slice()
                  .reverse()
                  .slice(0, 10)
                  .map((n, idx) => (
                    <div key={idx} className="rounded-xl border border-slate-200 p-3">
                      <div className="text-xs text-slate-500">{fmtDateTime(n?.createdAt)}</div>
                      <div className="mt-1 text-sm text-slate-800 whitespace-pre-wrap">{safeString(n?.text)}</div>
                    </div>
                  ))}
              </div>
            ) : (
              <div className="text-sm text-slate-400">No notes yet</div>
            )}

            {canMutate && (
              <div className="mt-4">
                <FieldLabel>Add a note</FieldLabel>
                <TextArea
                  className="min-h-[90px]"
                  value={noteText}
                  onChange={(e) => setNoteText(e.target.value)}
                  placeholder="Internal note..."
                />
                <button
                  onClick={onAddNote}
                  disabled={savingNote || !safeString(noteText).trim() || patching}
                  className={`mt-2 inline-flex items-center justify-center gap-2 px-3 py-2 rounded-xl border text-sm font-semibold w-full ${
                    savingNote || !safeString(noteText).trim() || patching
                      ? "bg-slate-100 text-slate-400 cursor-not-allowed"
                      : "bg-white hover:bg-slate-50 text-slate-900"
                  }`}
                  type="button"
                >
                  {savingNote ? "Saving…" : "Add note"}
                </button>
              </div>
            )}
          </Card>
        </div>

        {/* RIGHT (Linked Client + Workflow + Timeline) */}
        <div className="lg:col-span-4 space-y-4">
          {/* ✅ LINKING CARD ON RIGHT SIDE */}
          <Card
            title="Linked Client"
            right={<span className="text-xs text-slate-500">CRM</span>}
            className="relative"
          >
            {linkedClient.isLinked ? (
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-xs text-slate-500">Linked to</div>
                    <div className="mt-1 text-base font-extrabold text-slate-900 truncate">
                      {linkedClient.clientName || "Client"}
                    </div>
                    <div className="mt-2 space-y-1 text-sm">
                      {linkedClient.clientEmail ? (
                        <div className="text-slate-700 truncate">{linkedClient.clientEmail}</div>
                      ) : null}
                      {linkedClient.clientPhone ? (
                        <div className="text-slate-700 truncate">{linkedClient.clientPhone}</div>
                      ) : null}
                      <div className="text-xs text-slate-500">
                        Client ID:{" "}
                        <span className="font-mono text-slate-700">
                          {linkedClient.clientId.length > 14
                            ? `${linkedClient.clientId.slice(0, 6)}…${linkedClient.clientId.slice(-6)}`
                            : linkedClient.clientId}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="shrink-0 text-slate-500 mt-1">
                    <FiUser />
                  </div>
                </div>

                <div className="mt-4 flex flex-wrap gap-2">
                  <button
                    type="button"
                    className="inline-flex items-center justify-center gap-2 px-3 py-2 rounded-xl border bg-white hover:bg-slate-50 text-sm font-semibold"
                    onClick={() => navigate(`/clients/${linkedClient.clientId}`)}
                  >
                    View client <FiChevronRight />
                  </button>

                  {canMutate && (
                    <button
                      type="button"
                      className="inline-flex items-center justify-center gap-2 px-3 py-2 rounded-xl border border-rose-200 bg-rose-50 text-rose-700 text-sm font-semibold hover:bg-rose-100"
                      onClick={onUnlinkClient}
                      disabled={patching}
                      title="Unlink"
                    >
                      Unlink
                    </button>
                  )}
                </div>

                <div className="mt-3 text-xs text-slate-500">
                  This request is attached to the CRM client record (invoices, quotes, activity).
                </div>
              </div>
            ) : (
              <div>
                <div className="rounded-2xl border border-slate-200 bg-white p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-xs text-slate-500">Status</div>
                      <div className="mt-1 text-base font-extrabold text-slate-900">Not linked</div>
                      <div className="mt-2 text-sm text-slate-600">
                        Link this request to an existing CRM client (search by name, email, or phone).
                      </div>
                    </div>
                    <div className="shrink-0 text-slate-400 mt-1">
                      <FiUser />
                    </div>
                  </div>

                  {canMutate ? (
                    <>
                      <div className="mt-4">
                        <FieldLabel>Search clients</FieldLabel>
                        <TextInput
                          value={clientLinkQuery}
                          onChange={(e) => setClientLinkQuery(e.target.value)}
                          disabled={patching || creatingClientLink}
                          placeholder="Type at least 2 characters…"
                        />
                        <div className="mt-2 flex flex-wrap gap-2">
                          <button
                            type="button"
                            className="px-3 py-1.5 rounded-xl border bg-white hover:bg-slate-50 text-xs font-semibold"
                            onClick={() => onQuickSearchFromContact("name")}
                            disabled={patching || creatingClientLink}
                            title="Search by contact name"
                          >
                            Use name
                          </button>
                          <button
                            type="button"
                            className="px-3 py-1.5 rounded-xl border bg-white hover:bg-slate-50 text-xs font-semibold"
                            onClick={() => onQuickSearchFromContact("email")}
                            disabled={patching || creatingClientLink}
                            title="Search by contact email"
                          >
                            Use email
                          </button>
                          <button
                            type="button"
                            className="px-3 py-1.5 rounded-xl border bg-white hover:bg-slate-50 text-xs font-semibold"
                            onClick={() => onQuickSearchFromContact("phone")}
                            disabled={patching || creatingClientLink}
                            title="Search by contact phone"
                          >
                            Use phone
                          </button>
                        </div>
                      </div>

                      {clientLinkError ? (
                        <div className="mt-3 text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-xl p-3">
                          {clientLinkError}
                        </div>
                      ) : null}

                      <div className="mt-3">
                        {clientLinkLoading ? (
                          <div className="text-sm text-slate-500">Searching…</div>
                        ) : clientLinkQuery.trim().length >= 2 ? (
                          clientLinkResults.length ? (
                            <div className="rounded-2xl border border-slate-200 overflow-hidden">
                              {clientLinkResults.map((c) => (
                                <button
                                  key={c.id}
                                  type="button"
                                  className="w-full text-left px-4 py-3 bg-white hover:bg-slate-50 border-b last:border-b-0 border-slate-200"
                                  onClick={() => onLinkClient(c)}
                                  disabled={patching || creatingClientLink}
                                  title="Link this client"
                                >
                                  <div className="flex items-start justify-between gap-3">
                                    <div className="min-w-0">
                                      <div className="font-bold text-slate-900 truncate">{c.name}</div>
                                      <div className="mt-1 text-xs text-slate-500 truncate">
                                        {[c.email, c.phone].filter(Boolean).join(" • ") || "—"}
                                      </div>
                                      {c.company ? (
                                        <div className="mt-1 text-xs text-slate-500 truncate">
                                          {c.company}
                                        </div>
                                      ) : null}
                                    </div>
                                    <div className="shrink-0 text-slate-400 mt-1">
                                      <FiChevronRight />
                                    </div>
                                  </div>
                                </button>
                              ))}
                            </div>
                          ) : (
                            <div className="text-sm text-slate-500">
                              No clients found for “{clientLinkQuery.trim()}”.
                            </div>
                          )
                        ) : (
                          <div className="text-sm text-slate-500">Start typing to search.</div>
                        )}
                      </div>

                      {/* ✅ NEW: one-click create + link */}
                      <div className="mt-4 pt-4 border-t border-slate-200">
                        <button
                          type="button"
                          className={`w-full inline-flex items-center justify-center gap-2 px-3 py-2 rounded-xl border text-sm font-semibold ${
                            creatingClientLink || patching
                              ? "bg-slate-100 text-slate-400 cursor-not-allowed"
                              : "bg-slate-900 text-white hover:bg-slate-800 border-slate-900"
                          }`}
                          onClick={onCreateClientAndLink}
                          disabled={creatingClientLink || patching}
                          title="Create a new client from this request and link it"
                        >
                          <FiUser />
                          {creatingClientLink ? "Creating…" : "Create client & link"}
                        </button>
                        <div className="mt-2 text-xs text-slate-500">
                          Uses the name/email/phone/address from this request. (Backend should dedupe if it finds a match.)
                        </div>
                      </div>

                      <div className="mt-3 text-xs text-slate-500">
                        Tip: link it once and all future quotes/invoices can attach cleanly.
                      </div>
                    </>
                  ) : (
                    <div className="mt-4 text-sm text-slate-500">
                      You don’t have permission to link clients.
                    </div>
                  )}
                </div>
              </div>
            )}
          </Card>

          <Card title="Workflow" right={<span className="text-xs text-slate-500">Internal</span>}>
            <div className="space-y-3">
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <div className="text-xs text-slate-500">Assignee</div>
                    <div className="font-semibold text-slate-900">{derived.assignedName || "Unassigned"}</div>
                  </div>
                  <div className="text-slate-500 mt-1">
                    <FiUser />
                  </div>
                </div>

                {canMutate && (
                  <Select
                    value={form.assignedTo || ""}
                    onChange={(e) => {
                      const v = e.target.value || "";
                      setForm((f) => ({ ...f, assignedTo: v }));
                      onAssign(v || null);
                    }}
                    disabled={!canMutate || patching}
                  >
                    <option value="">Unassigned</option>
                    {meId && <option value={meId}>Assign to me</option>}
                    {Array.isArray(users) &&
                      users.map((u) => {
                        const uid = String(u?._id || u?.id || u?.userId || "");
                        if (!uid) return null;
                        const uname = u?.name || u?.fullName || u?.email || "User";
                        return (
                          <option key={uid} value={uid}>
                            {uname}
                          </option>
                        );
                      })}
                  </Select>
                )}
              </div>

              <div>
                <FieldLabel>Status</FieldLabel>
                {canMutate ? (
                  <Select
                    value={form.status}
                    disabled={patching}
                    onChange={(e) =>
                      onWorkflowChange({ status: e.target.value }, { status: e.target.value }, "Status updated")
                    }
                  >
                    {STATUS_OPTIONS.map((s) => (
                      <option key={s.value} value={s.value}>
                        {s.label}
                      </option>
                    ))}
                  </Select>
                ) : (
                  <div className="mt-2">
                    <Pill className={statusChipClass(form.status)}>{humanizeEnum(form.status)}</Pill>
                  </div>
                )}
              </div>

              <div>
                <FieldLabel>Priority</FieldLabel>
                {canMutate ? (
                  <Select
                    value={form.priority}
                    disabled={patching}
                    onChange={(e) => onWorkflowChange({ priority: e.target.value }, { priority: e.target.value })}
                  >
                    {PRIORITY_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </Select>
                ) : (
                  <div className="mt-2">
                    <Pill className={priorityChipClass(form.priority)}>{humanizeEnum(form.priority)}</Pill>
                  </div>
                )}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <FieldLabel>Source</FieldLabel>
                  <Select
                    value={form.source}
                    disabled={patching}
                    onChange={(e) => onWorkflowChange({ source: e.target.value }, { source: e.target.value })}
                  >
                    {SOURCE_OPTIONS.map((s) => (
                      <option key={s.value} value={s.value}>
                        {s.label}
                      </option>
                    ))}
                  </Select>
                </div>
                <div>
                  <FieldLabel>Channel</FieldLabel>
                  <Select
                    value={form.channel}
                    disabled={patching}
                    onChange={(e) => onWorkflowChange({ channel: e.target.value }, { channel: e.target.value })}
                  >
                    {CHANNEL_OPTIONS.map((c) => (
                      <option key={c.value} value={c.value}>
                        {c.label}
                      </option>
                    ))}
                  </Select>
                </div>
              </div>

              <div>
                <FieldLabel>Status reason (optional)</FieldLabel>
                <TextInput
                  value={form.statusReason}
                  onChange={(e) => setForm((f) => ({ ...f, statusReason: e.target.value }))}
                  onBlur={onBlurStatusReason}
                  disabled={!canMutate || patching}
                  placeholder="Optional"
                />
              </div>

              <div>
                <FieldLabel>Tags (comma separated)</FieldLabel>
                <TextInput
                  value={form.tags}
                  onChange={(e) => setForm((f) => ({ ...f, tags: e.target.value }))}
                  onBlur={onBlurTags}
                  disabled={!canMutate || patching}
                  placeholder="e.g. urgent, residential, hvac"
                />
                {safeString(form.tags).trim() ? (
                  <div className="mt-2 flex flex-wrap gap-2">
                    {tagsStringToArray(form.tags)
                      .slice(0, 10)
                      .map((t) => (
                        <span
                          key={t}
                          className="inline-flex items-center px-2 py-1 rounded-lg text-xs font-semibold bg-slate-100 text-slate-700 border border-slate-200"
                        >
                          {t}
                        </span>
                      ))}
                  </div>
                ) : null}
              </div>
            </div>
          </Card>

          <Card title="Timeline">
            <div className="space-y-2 text-sm">
              <div className="flex items-center justify-between">
                <span className="text-slate-500">Created</span>
                <span className="font-semibold text-slate-900">{fmtDateTime(derived.createdAt)}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-slate-500">Last updated</span>
                <span className="font-semibold text-slate-900">{fmtDateTime(derived.updatedAt)}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-slate-500">Time in status</span>
                <span className="font-semibold text-slate-900">{derived.timeInStatus}</span>
              </div>

              <div className="pt-2 text-xs text-slate-500">
                Source: <span className="font-semibold text-slate-700">{humanizeEnum(derived.source)}</span> • Channel:{" "}
                <span className="font-semibold text-slate-700">{humanizeEnum(derived.channel)}</span>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
